/**
 * conditionScale — SINGLE SOURCE OF TRUTH for card condition grades.
 *
 * Every consumer (pricing adapter, list modal, trade views, profile, map
 * sidebars) imports from here so the scale, colors, labels, and price
 * multipliers can never drift apart again.
 *
 * Scale rationale
 * ───────────────
 * We use the 5 tiers that real sales feeds (TCGplayer, eBay sold) actually
 * report: Near Mint · Lightly Played · Moderately Played · Heavily Played ·
 * Damaged. That means every grade can, in principle, be backed by verified
 * per-condition sales rather than a made-up multiplier.
 *
 * There is deliberately NO raw "Mint" grade: Near Mint is the ceiling for an
 * ungraded card. A raw-Mint premium has no sales data behind it — "Mint"
 * pricing only exists in the graded (PSA/BGS) market, which is a different
 * item entirely and out of scope here.
 *
 * Multipliers are ESTIMATES (a flat table). Real condition spreads widen for
 * vintage/expensive cards and narrow for modern commons, so any price derived
 * from a multiplier must be surfaced to the user as an estimate, not a fact.
 * Values are anchored to TCGplayer's published condition spreads.
 */

// Canonical grades, best → worst. `code` is the recognized TCG shorthand.
export const CONDITIONS = [
  {
    value: 'near_mint',
    label: 'Near Mint',
    shortLabel: 'NM',
    code: 'NM',
    desc: 'No or minimal\nimperfections.',
    multiplier: 1.0,
    color: '#22c55e',
  },
  {
    value: 'lightly_played',
    label: 'Lightly Played',
    shortLabel: 'LP',
    code: 'LP',
    desc: 'Minor edge wear\nor light scuffs.',
    multiplier: 0.85,
    color: '#66bb6a',
  },
  {
    value: 'moderately_played',
    label: 'Moderately Played',
    shortLabel: 'MP',
    code: 'MP',
    desc: 'Moderate wear,\nscratches or bends.',
    multiplier: 0.65,
    color: '#ffa726',
  },
  {
    value: 'heavily_played',
    label: 'Heavily Played',
    shortLabel: 'HP',
    code: 'HP',
    desc: 'Heavy wear,\ncreases or scuffing.',
    multiplier: 0.45,
    color: '#ef5350',
  },
  {
    value: 'damaged',
    label: 'Damaged',
    shortLabel: 'DMG',
    code: 'DMG',
    desc: 'Major damage —\ntears, water, ink.',
    multiplier: 0.3,
    color: '#9e9e9e',
  },
];

// Default grade for new listings — matches market convention (TCGplayer
// defaults listings to NM). The claim is always shown as "seller-stated".
export const DEFAULT_CONDITION = 'near_mint';

/**
 * Legacy grade values that may exist on older documents, mapped to their
 * closest canonical grade. Kept so old cards keep rendering and pricing
 * correctly, and so the backend enum can retain them without breakage.
 */
// #280, second half — found by a skeptic refuting the FIRST half's claim to have
// closed the class. `Object.assign(Object.create(null), {...})` rather than a bare
// object literal, for the same reason as the three derived maps below and with a
// sharper consequence: `normalizeCondition` ends
// `return LEGACY_CONDITION_MAP[value] || DEFAULT_CONDITION`, so with a
// `Object.prototype`-backed literal it returned a FUNCTION for 'toString',
// 'valueOf', 'constructor' and 'hasOwnProperty', and `Object.prototype` itself for
// '__proto__' — never the canonical grade string its own JSDoc promises. Eight call
// sites consume it and none crashed — but NOT because every one guards: it is
// `byValue`'s `|| CONDITIONS[0]` doing the absorbing, and both
// `tradeUiLogic.js:141` and `priceAdapter.js:169` consume the result with no
// guard of their own. Measured with the bare literal restored: `conditionLabel`,
// `conditionCode`, `conditionMultiplier`, `getConditionPrice`, `getAdjustedPrice`
// and `offeredCondition` over all TWELVE `Object.prototype` own keys threw zero
// times and every one degraded to Near Mint. The
// literal is preserved verbatim; only the prototype changes. The measured
// downstream consequence: `tradeUiLogic.js:141` returned that function as
// `offeredCondition`, and `JSON.stringify` DROPS a function value - so the
// condition silently vanished from the payload rather than being reported wrong. `Object.keys` and
// `Object.entries` behave identically on a null-prototype object. THREE sites
// read this map: `Object.keys` at :112 just below, the bracket read at :119
// inside `normalizeCondition` that this paragraph is about, and
// `Object.entries` in `tradeUiLogic.test.js:293`. (The first draft cited :96
// for the `Object.keys` line and omitted the bracket read entirely — and the
// cite was made stale by inserting this very comment above the line it pointed
// at, which is worth knowing as a failure mode.)
export const LEGACY_CONDITION_MAP = Object.assign(Object.create(null), {
  mint: 'near_mint', // raw Mint had no sales backing → treat as NM ceiling
  excellent: 'lightly_played',
  good: 'moderately_played',
  light_played: 'lightly_played', // never shipped, but be defensive
  played: 'heavily_played',
  poor: 'damaged',
});

/** Every value the backend enum should accept: canonical + legacy. */
export const ALL_CONDITION_VALUES = [
  ...CONDITIONS.map((c) => c.value),
  ...Object.keys(LEGACY_CONDITION_MAP),
];

/** Map any (possibly legacy) condition value to a canonical grade value. */
export const normalizeCondition = (value) => {
  if (!value) return DEFAULT_CONDITION;
  if (CONDITIONS.some((c) => c.value === value)) return value;
  return LEGACY_CONDITION_MAP[value] || DEFAULT_CONDITION;
};

const byValue = (value) =>
  CONDITIONS.find((c) => c.value === normalizeCondition(value)) || CONDITIONS[0];

// ── derived lookup maps (built from the single list above) ──────────────────
//
// #280: seeded with `Object.create(null)`, NOT `{}`. Eight of the nine reads of
// CONDITION_COLORS were written as `MAP[value] || fallback` when this was written
// — all nine are, since #282 closed the ninth — and a `{}` seed inherits
// `Object.prototype` — so `MAP.toString` is a FUNCTION, `MAP.valueOf` is a
// FUNCTION, and the `||` never reaches its fallback for that whole family of
// keys. Measured through `ListCardModal` before this change: `toString` and
// `constructor` rendered the grade badge with no background at all,
// `__proto__` produced `boxShadow: 0 4px 16px [object Object]66`, and
// `valueOf` threw `TypeError: Cannot convert undefined or null to object` out
// of `@mui/system/styleFunctionSx` and took the modal down. A null-prototype
// seed makes every non-grade key MISS, which is what those fallbacks assume.
//
// The ninth read HAD no fallback at all — `map/components/MarketplaceView.jsx`
// was a bare `bgcolor: CONDITION_COLORS[item.condition]`. That was still true
// when #280 shipped and is NOT true any more: #282 gave it `|| '#999'`, matching
// `UserSidebar.jsx:188` on the same surface, and
// `MarketplaceView.conditionChipColor.test.jsx` now pins it. All nine reads
// carry a fallback. Two things this paragraph used to say are worth keeping
// straight, because both were measured:
//   · The count. A skeptic corrected the first draft's "every consumer" to nine
//     reads across SEVEN files; a later skeptic corrected the correction, which
//     had said "eight files" only because it counted a concurrent skeptic's
//     scratch component that lived on disk for twenty minutes. Re-counted with
//     `git grep ... HEAD`, which cannot see an untracked file: READS=9, FILES=7.
//   · The symptom. This paragraph said the site was "degraded, not defended",
//     with the seed helping because "an `undefined` bgcolor is dropped by MUI
//     rather than thrown out of it". The drop is real, but #282 measured what
//     the drop actually LOOKED like and it was not a bare chip: `sx` omitting
//     the property leaves MUI's own default filled-Chip fill,
//     `rgba(0, 0, 0, 0.08)`, under that line's hard-coded white `color` — about
//     1.2:1, illegible rather than invisible.
//
// Scope, corrected after the refute round so this is not read as more than it
// is: CONDITION_LABELS and CONDITION_MULTIPLIERS have ZERO bracket readers
// anywhere in the app - a grep for either name followed by '[' returns nothing
// outside this file and the two pass-through re-exports, and `priceAdapter`
// uses the `conditionMultiplier()` FUNCTION, not the map. The user-visible
// defect exists only through CONDITION_COLORS. Seeding the other two is hygiene
// on exported API, not a fix to a live path.

export const CONDITION_MULTIPLIERS = ALL_CONDITION_VALUES.reduce((acc, v) => {
  acc[v] = byValue(v).multiplier;
  return acc;
}, Object.create(null));

export const CONDITION_COLORS = ALL_CONDITION_VALUES.reduce((acc, v) => {
  acc[v] = byValue(v).color;
  return acc;
}, Object.create(null));

export const CONDITION_LABELS = ALL_CONDITION_VALUES.reduce((acc, v) => {
  acc[v] = byValue(v).label;
  return acc;
}, Object.create(null));

/** Human label for a condition value, e.g. "Near Mint". */
export const conditionLabel = (value) => byValue(value).label;

/** Short code for a condition value, e.g. "NM". */
export const conditionCode = (value) => byValue(value).code;

/** Multiplier for a condition value (falls back to a conservative estimate). */
export const conditionMultiplier = (value) => byValue(value).multiplier;
